/**
 * Same-page fragment navigation, shared by the desktop nav item and the mobile
 * sidebar (e.g. the Website Builder "Contact section" link → `/#contact`).
 *
 * A link is only hijacked for an in-page smooth scroll when its resolved URL is
 * the CURRENT origin + pathname and carries a fragment. External URLs,
 * cross-route links (`/contact#contact`, `https://partner.example/#contact`),
 * and modified/middle clicks fall through to the browser's normal navigation so
 * `target="_blank"`, open-in-new-tab, etc. keep working.
 */

/**
 * Deterministic (SSR-safe) render gate: is `href` an APP-INTERNAL URL that
 * carries a fragment? True for a bare fragment (`#contact`) or any root-relative
 * path with a fragment (`/#contact`, `/?utm=x#contact`). False for external
 * (`https://…/#x`) and protocol-relative (`//host#x`) URLs. The actual
 * same-document decision still happens at click time via `resolveSamePageHash`
 * — this only decides whether to render the scrollable `<a>` vs a route link, so
 * it must not depend on `window` (that would cause a hydration mismatch).
 */
export function isInternalHashLink(href: string): boolean {
  if (href.startsWith('#')) return true;
  if (href.startsWith('//')) return false; // protocol-relative → external
  return href.startsWith('/') && href.includes('#');
}

/**
 * The fragment id iff `href` resolves to an anchor on the *current* document;
 * otherwise `null`. SSR-safe — returns `null` when there is no `window`, so the
 * caller's render decision must not depend on it (only its click handler).
 */
export function resolveSamePageHash(href: string): string | null {
  if (typeof window === 'undefined') return null;

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== window.location.pathname) return null;

  const id = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  return id.length > 0 ? id : null;
}

/**
 * A plain primary click with no modifier keys — the only case we may intercept.
 * Cmd/Ctrl/Shift/Alt or a non-primary button must open the link normally (new
 * tab, background tab, etc.), so those return `false`.
 */
export function isPlainClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Smooth-scrolls to the element with `id` when present, honoring
 * `prefers-reduced-motion` (WCAG 2.3.3) and moving focus into the section for
 * keyboard/AT users (so the next Tab continues from there, not the page top).
 * Returns `true` when it scrolled — the caller should then `preventDefault()`.
 */
export function scrollToHashTarget(id: string): boolean {
  if (typeof document === 'undefined') return false;

  const target = document.getElementById(id);
  if (!target) return false;

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });

  // tabIndex=-1 makes the section programmatically focusable without adding a
  // Tab stop; preventScroll avoids a second jump on top of scrollIntoView.
  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
  }
  target.focus({ preventScroll: true });

  window.history.replaceState(null, '', `#${id}`);
  return true;
}
